// `useFormat()` — THE formatting funnel (contract 04 §9; ledger §A10). The single path the shared
// UI primitives and every feature component use to render values; direct `Intl.*` /
// `.toLocaleString` / `.toFixed` in presentation code is a lint error (BRIEF §3.6).
//
// It is a THIN REACTIVE SHELL, not an implementation: it delegates to the framework-agnostic
// `createFormatter` (already on dev), binding it to the ACTIVE locale and the workspace currency.
//
// ── Reactivity ──────────────────────────────────────────────────────────────────────────────
// `createFormatter` accepts a locale GETTER; every formatter method resolves the locale at CALL
// time. Passing `activeIntlLocale` means a component that formats a value inside its render reads
// the reactive `i18n.global.locale` ref there and re-renders when `setLocale` swaps it — no
// `computed`/`watch` here (and, deliberately, no Vue import at all): this module stays inside the
// `shared/lib/format` purity boundary — no Vue, no Pinia, no store — proven by the barrel's purity
// spec.
//
// ── Isolation guarantee (contract §9, non-negotiable) ───────────────────────────────────────
// It must render correctly with NO i18n runtime installed at all (Histoire stories, isolated unit
// tests). The active Intl locale is read from the i18n instance if it has an installed locale,
// else it falls back to `DEFAULT_LOCALE`'s `intl` (`en-US`). The instance is a module singleton
// seeded to `DEFAULT_LOCALE`, so the fallback is the natural resting state — one code path covers
// both cases.
//
// ── Currency: ONE source (contract §5, §9, §14) ─────────────────────────────────────────────
// Legacy `useCellFormatter` read THREE sources for one currency (`workspaceStore.currency`,
// `dashboardStore`, an injected value — `format.js:386–389`). v4 has ONE: the currency passed to
// `useFormat({ currency })`. No store is imported here. When a caller injects none, the binding
// degrades to an explicit default (`DEFAULT_LOCALE.defaultCurrency`) — the BE-ACC-08/09 soft seam
// (contract §9), non-blocking.

import { createFormatter, type Formatter } from './createFormatter'

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, i18n, localeMeta } from '@/shared/i18n'
import type { SupportedLocale } from '@/shared/i18n'

/** Options for {@link useFormat}. `currency` is the single injected/optional currency source. */
export interface UseFormatConfig {
  /** ISO-4217 code (e.g. `'EUR'`) `formatCurrency` uses when a call passes no explicit currency. */
  currency?: string
}

function isSupportedLocale(tag: string): tag is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(tag)
}

/**
 * The active Intl BCP-47 tag, read reactively from the i18n instance. A locale outside
 * `SUPPORTED_LOCALES` (or the un-installed resting state) resolves to `DEFAULT_LOCALE`'s `intl`
 * (`en-US`) — the isolation fallback (contract §9).
 */
function activeIntlLocale(): string {
  const current: string = i18n.global.locale.value
  return isSupportedLocale(current) ? localeMeta[current].intl : localeMeta[DEFAULT_LOCALE].intl
}

/**
 * A reactive {@link NumberFormatter} bound to the active locale and (optionally) the workspace
 * currency. Formatted values re-derive when `setLocale` changes the active locale. It is a shell
 * over `createFormatter`; the pure per-domain formatters do the work.
 */
export function useFormat(config: UseFormatConfig = {}): Formatter {
  const currency = config.currency ?? localeMeta[DEFAULT_LOCALE].defaultCurrency
  return createFormatter(activeIntlLocale, { currency })
}
